import { useEffect, useState } from "react";
import { BookOpen, Copy, Check, Eye, FileText } from "lucide-react";
import { toast } from "sonner";

const pdfs = [
  { label: "One-pager White-label (Filata)", path: "/filata-onepager.pdf" },
  { label: "One-pager Luxury Events (EN)", path: "/filata-events-onepager.pdf" },
  { label: "One-pager Patrizia's (Giacomo)", path: "/patrizias-onepager.pdf" },
];

const exportsMd = [
  { label: "Export white-label (per Gemini)", path: "/filata-content.md" },
  { label: "Export luxury events (per Gemini)", path: "/filata-events-content.md" },
];

export default function Guida() {
  const [entries, setEntries] = useState(null);
  const [copied, setCopied] = useState("");
  const origin = window.location.origin;
  const key = new URLSearchParams(window.location.search).get("k") || sessionStorage.getItem("filata-code") || "";

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guide?key=${encodeURIComponent(key)}`)
      .then((r) => { if (!r.ok) throw new Error("unauthorized"); return r.json(); })
      .then(setEntries)
      .catch(() => setEntries([]));
  }, [key]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      toast.success("Copiato negli appunti");
      setTimeout(() => setCopied(""), 1500);
    });
  };

  return (
    <div className="min-h-screen bg-wine text-bg px-6 md:px-10 py-14" data-testid="guida-page">
      <div className="max-w-4xl mx-auto">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-terracotta mb-3 flex items-center gap-2">
          <BookOpen className="h-4 w-4" /> Guida rapida riservata
        </p>
        <h1 className="font-serif text-4xl md:text-5xl mb-10">Tutti i tuoi link e codici</h1>

        {entries === null ? (
          <p className="font-sans text-bg/60">Caricamento…</p>
        ) : entries.length === 0 ? (
          <p className="font-sans text-terracotta" data-testid="guida-unauthorized">
            Accesso non autorizzato — apri questa pagina con il tuo codice personale (?k=...).
          </p>
        ) : (
          <>
            <div className="space-y-4" data-testid="guida-links">
              {entries.map((e) => {
                const url = `${origin}${e.path}?k=${e.code}`;
                return (
                  <div key={e.code} className="border border-bg/15 rounded-sm px-5 py-4">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-serif text-xl text-terracotta">{e.label}</span>
                      {e.one_time && (
                        <span className={`font-sans text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${e.used ? "border-terracotta text-terracotta" : "border-bg/30 text-bg/60"}`}>
                          {e.used ? "monouso · già usato" : "monouso · attivo"}
                        </span>
                      )}
                      {e.silent && (
                        <span className="font-sans text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border border-bg/30 text-bg/60">
                          silenzioso
                        </span>
                      )}
                      <button
                        data-testid={`copy-${e.code}`}
                        onClick={() => copy(url, e.code)}
                        className="ml-auto inline-flex items-center gap-1.5 font-sans text-xs text-bg/70 hover:text-bg border border-bg/25 rounded-full px-3 py-1.5 transition-colors"
                      >
                        {copied === e.code ? <Check className="h-3.5 w-3.5 text-terracotta" /> : <Copy className="h-3.5 w-3.5" />} Copia link
                      </button>
                    </div>
                    <p className="font-sans text-sm text-bg/50 mt-2 break-all">{url}</p>
                  </div>
                );
              })}
            </div>

            <h2 className="font-serif text-2xl mt-12 mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-terracotta" /> PDF da allegare</h2>
            <div className="space-y-2 font-sans text-sm">
              {pdfs.map((p) => (
                <div key={p.path} className="flex flex-wrap items-center gap-3">
                  <a href={p.path} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">{p.label}</a>
                  <button onClick={() => copy(`${origin}${p.path}`, p.path)} className="text-xs text-bg/50 hover:text-bg">copia URL</button>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-2xl mt-10 mb-4">Export per Gemini</h2>
            <div className="space-y-2 font-sans text-sm">
              {exportsMd.map((p) => (
                <div key={p.path} className="flex flex-wrap items-center gap-3">
                  <a href={p.path} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">{p.label}</a>
                  <button onClick={() => copy(`${origin}${p.path}`, p.path)} className="text-xs text-bg/50 hover:text-bg">copia URL</button>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-2xl mt-10 mb-4 flex items-center gap-2"><Eye className="h-5 w-5 text-terracotta" /> Dashboard</h2>
            <p className="font-sans text-sm">
              <a href={`/visite?k=${key}`} className="text-terracotta hover:text-bg transition-colors">Registro aperture e richieste → /visite</a>
            </p>

            <h2 className="font-serif text-2xl mt-10 mb-4">Documenti riservati</h2>
            <p className="font-sans text-sm" data-testid="guida-aruba-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/aruba-project?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Aruba Project — Business Proposal (PDF) ↓</a>
            </p>
            <p className="font-sans text-sm mt-2" data-testid="guida-marketing-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/aruba-marketing?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Aruba Project — Marketing &amp; Storytelling Toolkit (PDF) ↓</a>
            </p>
            <p className="font-sans text-sm mt-2" data-testid="guida-proposal-it-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/aruba-proposal-it?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Aruba — Business Proposal corporate IT (PDF) ↓</a>
            </p>
            <p className="font-sans text-sm mt-2" data-testid="guida-proposal-en-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/aruba-proposal-en?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Aruba — Business Proposal corporate EN (PDF) ↓</a>
            </p>
            <p className="font-sans text-sm mt-2" data-testid="guida-storytelling-it-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/storytelling-it?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Filata — Storytelling Toolkit IT (PDF) ↓</a>
            </p>
            <p className="font-sans text-sm mt-2" data-testid="guida-storytelling-en-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/storytelling-en?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Filata — Storytelling Toolkit EN (PDF) ↓</a>
            </p>
            <p className="font-sans text-sm mt-2" data-testid="guida-arina-pdf">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/private/arina-proposal?key=${encodeURIComponent(key)}`} target="_blank" rel="noreferrer" className="text-terracotta hover:text-bg transition-colors">Aruba — Proposal ARINA / Partnership EN (PDF) ↓</a>
            </p>

            <p className="font-sans text-xs text-bg/40 mt-12">
              Pagina riservata, non linkata dal sito. I link usano il dominio da cui apri questa pagina: dopo il collegamento di filataexperience.com saranno automaticamente sul dominio definitivo. Le email di avviso arrivano per ogni apertura (tranne il codice silenzioso).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
